import { DiseaseCategoryType } from "fe-common/graphql";
import { useEffect, useRef, useState } from "react";
import { ClusteredSuggestions, Suggestion } from "./hooks/useSuggestions";
import { getProcedurePanels } from "./useDataInputPanels";
import { useDaoContext } from "fe-web-components/diseaseview/DaoContext";
import { KahunPanelGroup, KahunPanelSubGroup } from "shared";
import { DataInputPanel } from "../dataInputTypes";

export type PlanPanel = {
  panel: DataInputPanel;
  suggestions: Suggestion[];
};

export type LabSuggestionsClusterPanels = {
  title: string;
  items: PlanPanel[];
};

export const useBuildPlanPanels = (
  suggestions: Suggestion[],
  clusteredSuggestions: ClusteredSuggestions[],
  categoryType: DiseaseCategoryType,
): {
  labSuggestionsPanels: LabSuggestionsClusterPanels[];
  loading: boolean;
} => {
  const [labSuggestionsPanels, setLabSuggestionPanels] = useState<
    LabSuggestionsClusterPanels[]
  >([]);

  const [loading, setLoading] = useState(false);

  const isMountedRef = useRef(true);

  const dao = useDaoContext();

  useEffect(() => {
    (async () => {
      setLabSuggestionPanels([]);
      setLoading(true);

      const labsSuggestions = suggestions.filter(
        (s) => s.category === categoryType,
      );

      let labsPanelsList: LabSuggestionsClusterPanels[] = [];
      let noPanelSuggestions = [];

      labsSuggestions.forEach((suggestion) => {
        const panel =
          suggestion.panelIds.length > 0 ? suggestion.panelIds[0] : null;

        const panelExists = labsPanelsList.find((lab) =>
          lab.items.some(
            (labItem) =>
              labItem.panel && panel && labItem.panel.id === panel.id,
          ),
        );
        if (!panel || !panelExists) {
          const panelSuggestions = panel
            ? suggestions.filter(
                (s) =>
                  (panel
                    ? s.panelIds.some((p) => p.id === panel.id)
                    : s.panelIds.length === 0) && s.category === categoryType,
              )
            : [suggestion];

          if (panel) {
            labsPanelsList.push({
              title: "",
              items: [
                {
                  panel: panel,
                  suggestions: panelSuggestions,
                },
              ],
            });
          } else {
            noPanelSuggestions = [...noPanelSuggestions, ...panelSuggestions];
          }
        }
      });

      if (categoryType === DiseaseCategoryType.Imaging) {
        labsPanelsList = [];
        const procedurePanels = await Promise.all(
          clusteredSuggestions.map((c) =>
            getProcedurePanels(
              c.suggestions.map((s) => s.finding),
              dao,
            ),
          ),
        );

        clusteredSuggestions.forEach((c, index) => {
          const items = procedurePanels[index].map((p, idx) => ({
            panel: p,
            suggestions: [c.suggestions[idx]],
          }));
          labsPanelsList.push({
            title: items.length > 1 ? c.title.name : "",
            items,
          });
        });
      } else {
        noPanelSuggestions.forEach((suggestion: Suggestion) => {
          const newPanel = {
            title: suggestion.finding.feature.name,
            id: KahunPanelSubGroup.Other,
            findings: [suggestion.finding],
            customFindings: [],
            panelGroup: {
              id: KahunPanelGroup.WORKUP,
              name: KahunPanelGroup.WORKUP,
              shortname: KahunPanelGroup.WORKUP,
            },
            panelSubgroup: {
              id:
                categoryType.toString() === DiseaseCategoryType.Imaging
                  ? KahunPanelSubGroup.Procedures
                  : KahunPanelSubGroup.Labs,
              name: "Procedures",
              shortname: "Kahun Procedures SUB group",
            },
          };

          labsPanelsList.push({
            title: "",
            items: [
              {
                panel: newPanel,
                suggestions: [suggestion],
              },
            ],
          });
        });
      }
      setLabSuggestionPanels(labsPanelsList);
      setLoading(false);
    })();
  }, [categoryType, dao, suggestions, clusteredSuggestions, isMountedRef]);

  return { labSuggestionsPanels, loading };
};
